import * as React from 'react';
import moment from 'moment';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { withStyles, Typography, Button } from '@material-ui/core';
import _ from 'lodash';
import Carousel from 'nuka-carousel';
import { isIOS, isChrome } from 'react-device-detect';

import {
  getListingPhotoList,
  checkStatusLive,
  addressString,
  removeMissingPhotos,
  normalizePrice,
  formatBathrooms,
} from '../../../lib/helpers';
import { isTablet, isMobile, isWeb } from '../../../lib/getDeviceSize';
import GoogleMaps from './Map';
import Disclaimer from './Disclaimer';
import ToriiLoader from '../../utility/ToriiLoader';
import ResponsiveDialog from '../../utility/ResponsiveDialog';
import { LeftNavIcon, RightNavIcon, ExitIcon } from '../../utility/Icons';
import styles from './styles';

const ResponsiveReactGridLayout = WidthProvider(Responsive);
const yAxisCalculator = (i: number) => {
  const multiplied = i * 5;
  if (i % 5 === 0) {
    return multiplied;
  } else {
    return multiplied - 10;
  }
};

interface Props {
  classes: any;
  currentImageIndex: number;
  contentHeight: number;
  favorites: any;
  handle3DView: Function;
  handleHideMap: Function;
  handleShowMap: Function;
  handleStreetView: Function;
  handleViewerClose: Function;
  handleViewerOpen: Function;
  listing: any;
  onImageChange: Function;
  renderIcons: any;
  router: any;
  show3DView: boolean;
  showMap: boolean;
  showStreetView: boolean;
  viewerIsOpen: boolean;
  listingModalOpen?: boolean;
}

interface State {
  currentImage: number;
  height: number;
  mounted: boolean;
  layouts: any;
  width: number;
}

export class Photos extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      currentImage: this.props.currentImageIndex,
      height: 500,
      layouts: this.generateLayout(),
      mounted: false,
      width: 350,
    };
  }

  listingPhotos = removeMissingPhotos(
    getListingPhotoList(this.props.listing, isMobile() ? 'tall' : 'large'),
  );

  generateComponentArray = () => {
    const componentProps: any = this.listingPhotos.map((photo: string) => {
      return { image: true, src: photo, map: false };
    });
    // Inserting Map component into componentProps array
    if (!isMobile()) {
      componentProps.splice(1, 0, { image: false, map: true });
    }
    return componentProps;
  };
  components = this.generateComponentArray();

  componentDidMount() {
    if (typeof window !== 'undefined') {
      this.updateWindowDimensions();
      window.addEventListener('resize', this.updateWindowDimensions);
      document.addEventListener('keydown', this.handleKeyPress, false);
    }
  }

  updateWindowDimensions = () => {
    this.setState({
      height:
        isIOS && isChrome
          ? document.documentElement.clientHeight
          : window.innerHeight,
      layouts: this.generateLayout(),
      mounted: true,
      width:
        isIOS && isChrome
          ? document.documentElement.clientWidth
          : window.innerWidth,
    });
  };

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
    document.removeEventListener('keydown', this.handleKeyPress, false);
  }

  handleKeyPress = (e: any) => {
    if (e.keyCode === 39) {
      // Right arrow
      this.nextPhoto();
    } else if (e.keyCode === 37) {
      // Left arrow
      this.previousPhoto();
    } else if (e.keyCode === 27) {
      // Escape key
      if (this.props.viewerIsOpen) {
        this.closeViewer();
      } else if (this.props.showMap) {
        this.props.handleHideMap();
      } else if (this.props.show3DView) {
        this.props.handle3DView(false);
      } else if (this.props.showStreetView) {
        this.props.handleStreetView(false);
      }
    }
  };

  openViewer = (index: number) => {
    this.setState({
      currentImage: index,
    });
    this.props.handleViewerOpen(index);
  };

  closeViewer = () => {
    this.props.handleViewerClose();
  };

  showMap = () => {
    this.props.handleShowMap();
  };
  handle3DPress = (open: boolean) => {
    this.props.handle3DView(open);
  };

  handleStreetView = (open: boolean) => {
    this.props.handleStreetView(open);
  };

  closeMap = () => {
    if (this.props.showMap) {
      this.props.handleHideMap();
    } else if (this.props.show3DView) {
      this.props.handle3DView(false);
    } else if (this.props.showStreetView) {
      this.props.handleStreetView(false);
    }
  };

  nextPhoto = () => {
    if (this.props.currentImageIndex === this.listingPhotos.length - 1) {
      this.props.onImageChange(0);
    } else {
      this.props.onImageChange(this.props.currentImageIndex + 1);
    }
  };

  previousPhoto = () => {
    if (this.props.currentImageIndex === 0) {
      this.props.onImageChange(this.listingPhotos.length - 1);
    } else {
      this.props.onImageChange(this.props.currentImageIndex - 1);
    }
  };

  generateLayout = () => {
    const width = isTablet() ? 2 : isMobile() ? 3 : 3;
    const height = isMobile() ? 6 : 8.25;
    // If mobile device and in Portrait mode
    if (isMobile() && window.orientation === 0) {
      return _.map(_.range(0, this.components.length), (item, i) => {
        return {
          h: height,
          i: i.toString(),
          w: 2,
          x: 0,
          y: i * 10,
        };
      });
    } else {
      return _.map(_.range(0, this.components.length), (item, i) => {
        if (i === this.components.length - 1) {
          // Making the last image fit on the opposite side
          if (i % 2 === 0) {
            return {
              h: height,
              i: i.toString(),
              w: width,
              x: 3,
              y: i * 5 - 5,
            };
          }
        }
        if (i === 0) {
          return {
            h: height,
            i: i.toString(),
            w: width,
            x: 0,
            y: 0,
          };
        } else if (i === 1) {
          return {
            h: isMobile() ? 6 : 4.15,
            i: i.toString(),
            w: width,
            x: 3,
            y: 0,
          };
        } else {
          if (i % 2 === 0) {
            return {
              h: height,
              i: i.toString(),
              w: width,
              x: 0,
              y: yAxisCalculator(i),
            };
          } else {
            return {
              h: height,
              i: i.toString(),
              w: width,
              x: 3,
              y: yAxisCalculator(i),
            };
          }
        }
      });
    }
  };

  generateDOM = (componentProps: any) => {
    const { classes, listing } = this.props;
    const activeStatus = checkStatusLive(listing);
    // Open house
    let openHouseOrUpdate = `UPDATED ${moment(listing.updateDate)
      .fromNow()
      .toLocaleUpperCase()}`;
    if (
      listing.openHouses &&
      listing.openHouses.length > 0 &&
      moment(listing.openHouses[0].date) > moment()
    ) {
      const { date, duration } = listing.openHouses[0];
      const time = moment.utc(date);
      openHouseOrUpdate = `OPEN ${moment(time)
        .format('dddd')
        .toLocaleUpperCase()}\n${time.format('h:mmA')} - ${time
        .add(duration, 'minutes')
        .format('h:mmA')}`;
    }
    const address = addressString(listing, true);

    const componentsToBeRendered = componentProps.map(
      (item: any, i: number) => {
        if (i === 0) {
          return (
            <div
              key={i}
              className={`static ${classes.photosContainerStyle}`}
              onClick={() => this.openViewer(i)}
            >
              <div className={classes.savingsContainer}>
                <div
                  className={classes.savingsInfoBox}
                  style={{ backgroundColor: 'rgba(0,0,0,0.75)', marginTop: 5 }}
                >
                  <Typography className={classes.savingsText}>
                    {openHouseOrUpdate}
                  </Typography>
                </div>
              </div>
              <img
                className={classes.photoStyle}
                alt={`${address} - Photo #${i + 1}`}
                src={item.src}
                style={isMobile() ? { width: this.state.width } : {}}
              />
            </div>
          );
        } else if (item.map) {
          const { favorites, router } = this.props;
          return (
            <div key={i} className={`static ${classes.photosContainerStyle}`}>
              <GoogleMaps
                listing={this.props.listing}
                favorites={favorites}
                router={router}
                onMapPress={this.showMap}
                on3DPress={this.handle3DPress}
                onStreetViewPress={this.handleStreetView}
                listingModalOpen={this.props.listingModalOpen}
              />
            </div>
          );
        } else if (item.image) {
          return (
            <div
              key={i}
              className={`static ${classes.photosContainerStyle}`}
              onClick={() => this.openViewer(isMobile() ? i : i - 1)}
            >
              <img
                alt={`${address} - Photo #${i + 1}`}
                className={classes.photoStyle}
                src={item.src}
                style={isMobile() ? { width: this.state.width } : {}}
              />
            </div>
          );
        } else if (item.component) {
          return <React.Fragment key={i}>{item.component}</React.Fragment>;
        } else {
          return null;
        }
      },
    );
    return componentsToBeRendered;
  };

  _renderCount = (dark?: boolean) => {
    return (
      <Typography
        className={this.props.classes.countStyle}
        style={{ color: dark ? '#323232' : '#FFFFFF' }}
      >
        {this.state.currentImage + 1} / {this.listingPhotos.length}
      </Typography>
    );
  };

  _renderDetails = () => {
    const { classes, listing, renderIcons } = this.props;
    return (
      <div className={classes.mobileDetailsContainer}>
        <div className={classes.mobileDetailsContent}>
          <Typography className={classes.priceStyle} style={{ color: 'white' }}>
            $
            {normalizePrice(
              listing.salePrice ? listing.salePrice : listing.listPrice,
            )}
          </Typography>
          {!isMobile() && (
            <Typography
              className={classes.detailsStyle}
              style={{ color: 'white', marginLeft: 25 }}
            >
              {listing.noBedrooms} br | {formatBathrooms(listing)} ba |{' '}
              {listing.squareFeet ? normalizePrice(listing.squareFeet) : '--'}{' '}
              sqft
            </Typography>
          )}
          {renderIcons(true)}
        </div>
      </div>
    );
  };

  createCarousel = (height: any) => {
    return this.listingPhotos.map((photo: any, index: number) => (
      <React.Fragment key={index}>
        <div className={this.props.classes.carouselPhotoLoader}>
          <ToriiLoader />
        </div>
        <div
          className={this.props.classes.carouselPhoto}
          style={
            isMobile()
              ? {
                  backgroundImage: `url(${photo})`,
                  backgroundSize: isMobile() ? 'auto 100%' : '100% 100%',
                  height: this.state.height,
                  width: this.state.width,
                }
              : { backgroundImage: `url(${photo})`, height }
          }
        />
      </React.Fragment>
    ));
  };

  _renderCarousel = (enableSwiping: boolean = false, style: any = {}) => {
    if (!this.listingPhotos || this.listingPhotos.length === 0) {
      return null;
    }
    return (
      <Carousel
        afterSlide={(index: number) => {
          this.setState({ currentImage: index });
        }}
        autoplay={false}
        cellAlign="center"
        cellSpacing={isMobile() ? 0 : 50}
        className="swiper-carousel"
        defaultControlsConfig={{
          pagingDotsStyle: {
            display: 'none',
          },
        }}
        dragging={false}
        heightMode="max"
        initialSlideHeight={style.height}
        pauseOnHover={false}
        slideIndex={this.props.currentImageIndex}
        slidesToShow={1}
        swiping={enableSwiping}
        withoutControls
        speed={0}
        style={style}
        wrapAround
      >
        {this.createCarousel(style.height)}
      </Carousel>
    );
  };

  render() {
    const { width, height } = this.state;
    const {
      classes,
      contentHeight,
      favorites,
      listing,
      router,
      show3DView,
      showMap,
      showStreetView,
      viewerIsOpen,
      listingModalOpen,
    } = this.props;
    if (!listing || Object.keys(listing).length === 0) {
      return null;
    }
    const carouselContentHeight = contentHeight;

    const componentProps = this.components;
    return (
      <div
        className={
          show3DView || showMap || showStreetView || viewerIsOpen
            ? classes.mapCarouselContainer
            : classes.photosCarouselContentContainer
        }
        style={
          isMobile()
            ? {
                width,
                height: carouselContentHeight,
                paddingTop: 0,
                paddingBottom: !viewerIsOpen ? 50 : 10,
              }
            : {
                height: carouselContentHeight,
                paddingTop:
                  show3DView || showMap || showStreetView || viewerIsOpen
                    ? 0
                    : isMobile()
                    ? 0
                    : listingModalOpen
                    ? 5
                    : 30,
              }
        }
      >
        {((!show3DView && !showMap && !showStreetView && isMobile()) ||
          (!show3DView && !showMap && !showStreetView && !viewerIsOpen)) && (
          <ResponsiveReactGridLayout
            className="layout"
            compactType="vertical"
            isDraggable={false}
            isResizable={false}
            layouts={{ lg: this.state.layouts }}
            measureBeforeMount={true}
            rowHeight={30}
            useCSSTransforms={isMobile() ? false : this.state.mounted}
          >
            {this.generateDOM(componentProps)}
          </ResponsiveReactGridLayout>
        )}
        {(showMap || show3DView || showStreetView) && (
          <div
            key={`${contentHeight}`}
            className={classes.mapContainer}
            style={{
              height: isMobile() ? contentHeight - 40 : contentHeight,
            }}
          >
            <GoogleMaps
              fullScreen
              show3DView={show3DView}
              showStreetView={showStreetView}
              listing={listing}
              favorites={favorites}
              router={router}
              onMapPress={this.showMap}
              closeFullScreen={this.closeMap}
              on3DPress={this.handle3DPress}
              onStreetViewPress={this.handleStreetView}
            />
          </div>
        )}
        {viewerIsOpen && !isMobile() && (
          <React.Fragment>
            {isWeb() && <div className={classes.bottomShadowBlack} />}
            <div
              className={classes.photoViewerContainer}
              style={{ height: carouselContentHeight }}
            >
              <Button
                className={classes.photosCloseButton}
                onClick={this.closeViewer}
                style={{ backgroundColor: '#fff' }}
              >
                <ExitIcon />
              </Button>
              <div
                key={carouselContentHeight}
                style={{ height: carouselContentHeight, width: '100%' }}
              >
                {this._renderCarousel(false, { height: carouselContentHeight })}
              </div>
              {isTablet() && (
                <React.Fragment>
                  <div className={classes.countContainer}>
                    {this._renderCount(true)}
                  </div>
                  <div
                    className={classes.photoNavButtonsContainer}
                    style={{ left: 15 }}
                  >
                    <Button
                      onClick={this.previousPhoto}
                      className={classes.photoNavButtons}
                    >
                      <LeftNavIcon />
                    </Button>
                  </div>
                  <div
                    className={classes.photoNavButtonsContainer}
                    style={{ right: 15 }}
                  >
                    <Button
                      onClick={this.nextPhoto}
                      className={classes.photoNavButtons}
                    >
                      <RightNavIcon />
                    </Button>
                  </div>
                </React.Fragment>
              )}
              {!isTablet() && (
                <div className={classes.photoCounterContainer}>
                  <Button
                    onClick={this.previousPhoto}
                    className={classes.photoNavButtons}
                  >
                    <LeftNavIcon small />
                  </Button>
                  {this._renderCount(true)}
                  <Button
                    onClick={this.nextPhoto}
                    className={classes.photoNavButtons}
                  >
                    <RightNavIcon small />
                  </Button>
                </div>
              )}
            </div>
          </React.Fragment>
        )}
        {viewerIsOpen && isMobile() && (
          <ResponsiveDialog
            className={classes.modalContianer}
            largePaper
            onClose={this.closeViewer}
            buttonClassName={classes.buttonClassName}
            open={this.props.viewerIsOpen && isMobile()}
            overrideFullScreen
          >
            {this._renderCarousel(true, {
              height: '100%',
              position: 'relative',
            })}
            <div className={classes.countContainer}>
              {this._renderCount(true)}
            </div>
            {this._renderDetails()}
          </ResponsiveDialog>
        )}
        <Disclaimer listing={listing} />
      </div>
    );
  }
}

export default withStyles(styles)(Photos);
